import React from "react";

const DOMAINS = [
  { label: "Emotional State & Awareness", params: ["Emotional Stability", "Stress Responsiveness", "Emotional Awareness", "Emotional Regulation"] },
  { label: "Motivation & Engagement",     params: ["Intrinsic Motivation", "Task Engagement", "Effort Sustainability", "Curiosity Orientation"] },
  { label: "Behaviour & Self-Management", params: ["Behavioural Consistency", "Impulse Regulation", "Adaptability", "Self-Discipline"] },
  { label: "Social & Relational",         params: ["Social Comfort", "Collaboration", "Social Sensitivity", "Communication Confidence"] },
  { label: "Resilience & Growth",         params: ["Resilience", "Confidence Stability", "Growth Orientation", "Environmental Alignment"] },
];

export default function ReportView({ data, onDownload, downloadLoading }) {
  if (!data) return null;

  const { learner, assessment, categoryScores, narrative } = data;

  const assessDate = assessment?.assessment_date
    ? new Date(assessment.assessment_date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

  const scoreMap = {};
  for (const c of (categoryScores || [])) {
    scoreMap[c.category_name] = Math.round((c.category_score / 4) * 100);
  }

  const hasScores = Object.keys(scoreMap).length > 0;
  const hasNarrative = narrative && (narrative.overall_snapshot || narrative.strength_signals?.length);

  return (
    <div style={s.wrap}>
      <h1 style={s.title}>BeIntelli Assessment Report</h1>

      <div style={s.meta}>
        <Row label="Student"         value={learner?.full_name || learner?.username || "—"} />
        <Row label="Grade"           value={`${learner?.grade || "—"} | Age Group: ${learner?.age_group || "—"}`} />
        <Row label="School"          value={learner?.school || "—"} />
        <Row label="Assessment Date" value={assessDate} />
        {assessment?.overall_status && (
          <Row label="Engagement Status" value={assessment.overall_status} />
        )}
      </div>

      <hr style={s.divider} />

      {/* DOMAIN OVERVIEW — domain averages only, no individual parameters */}
      {hasScores && (
        <Section title="Domain Overview">
          {DOMAINS.map((domain) => {
            const domainScores = domain.params.map(p => scoreMap[p]).filter(v => v != null);
            const avg = domainScores.length
              ? Math.round(domainScores.reduce((a, b) => a + b, 0) / domainScores.length)
              : null;
            if (avg == null) return null;
            return (
              <div key={domain.label} style={s.domainRow}>
                <span style={s.domainLabel}>{domain.label}</span>
                <div style={s.barBg}>
                  <div style={{ ...s.barFill, width: `${avg}%`, background: barColor(avg) }} />
                </div>
                <span style={s.domainAvg}>{avg}%</span>
              </div>
            );
          })}
        </Section>
      )}

      {/* AI NARRATIVE SECTIONS — show if available */}
      {hasNarrative && (
        <>
          <Section title="Overall Snapshot">
            <p style={s.bodyText}>{narrative.overall_snapshot}</p>
            {narrative.status && <p style={s.status}>Status: {narrative.status}</p>}
          </Section>

          {narrative.strength_signals?.length > 0 && (
            <Section title="Strength Signals">
              <ol style={s.list}>
                {narrative.strength_signals.map((item, i) => <li key={i} style={s.li}>{item}</li>)}
              </ol>
            </Section>
          )}

          {narrative.attention_areas?.length > 0 && (
            <Section title="Attention Areas">
              <ol style={s.list}>
                {narrative.attention_areas.map((item, i) => <li key={i} style={s.li}>{item}</li>)}
              </ol>
            </Section>
          )}

          {narrative.domain_insights?.length > 0 && (
            <Section title="Domain Insights">
              <ol style={s.list}>
                {narrative.domain_insights.map((d, i) => (
                  <li key={i} style={s.li}><strong>{d.domain}:</strong> {d.insight}</li>
                ))}
              </ol>
            </Section>
          )}

          {narrative.parent_guidance?.length > 0 && (
            <Section title="Parent Guidance">
              <ol style={s.list}>
                {narrative.parent_guidance.map((g, i) => <li key={i} style={s.li}>{g}</li>)}
              </ol>
            </Section>
          )}
        </>
      )}

      {!hasScores && !hasNarrative && (
        <p style={s.empty}>No assessment data available yet. Complete an assessment to view the report.</p>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
        <button style={s.dlBtn} onClick={onDownload} disabled={downloadLoading}>
          {downloadLoading ? "Generating PDF…" : "⬇ Download PDF"}
        </button>
      </div>

      <p style={s.note}>
        This report provides observational insights and does not constitute medical or psychological diagnosis.
      </p>
    </div>
  );
}

function barColor(pct) {
  if (pct >= 75) return "#16a34a";
  if (pct >= 50) return "#ca8a04";
  return "#dc2626";
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h2 style={sec.title}>{title}</h2>
      <div style={sec.line} />
      <div style={{ marginTop: 10 }}>{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", marginBottom: 4, fontSize: 13 }}>
      <strong style={{ minWidth: 155, color: "#1e293b" }}>{label}:</strong>
      <span style={{ color: "#334155" }}>{value}</span>
    </div>
  );
}

const s = {
  wrap:        { background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "36px 40px", maxWidth: 720, margin: "0 auto" },
  title:       { fontSize: 22, fontWeight: 800, textAlign: "center", marginBottom: 20, color: "#1e293b" },
  meta:        { marginBottom: 20 },
  divider:     { border: "none", borderTop: "1px solid #e2e8f0", margin: "0 0 24px" },
  domainRow:   { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  domainLabel: { fontSize: 13, fontWeight: 700, color: "#1e293b", minWidth: 210, flex: "none" },
  domainAvg:   { fontSize: 13, fontWeight: 700, color: "#475569", minWidth: 42, textAlign: "right" },
  barBg:       { flex: 1, height: 10, background: "#e2e8f0", borderRadius: 5, overflow: "hidden" },
  barFill:     { height: "100%", borderRadius: 5, transition: "width 0.3s" },
  bodyText:    { lineHeight: 1.75, color: "#334155", fontSize: 13 },
  status:      { fontStyle: "italic", fontWeight: 700, marginTop: 10, fontSize: 13 },
  list:        { paddingLeft: 0, listStyle: "none", margin: 0 },
  li:          { padding: "4px 0", fontSize: 13, lineHeight: 1.65, color: "#334155" },
  empty:       { color: "#94a3b8", fontSize: 13, textAlign: "center", padding: "24px 0" },
  dlBtn:       { padding: "10px 24px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  note:        { marginTop: 20, fontSize: 11, color: "#94a3b8", borderTop: "1px solid #e2e8f0", paddingTop: 12 },
};

const sec = {
  title: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 4 },
  line:  { height: 2, background: "#1e293b", borderRadius: 1 },
};
